import type { User } from 'oidc-client-ts';
import { authMode } from '@/lib/env';
import { CALLBACK_PATH, userManagerFor } from './oidc';
import {
  forgetTenant,
  initialsOf,
  lastTenant,
  publishSession,
  rememberTenant,
  setPendingSignIn,
  takePendingSignIn,
  type PendingSignIn,
  type Session,
} from './session';

export type CompletedSignIn = { session: Session; returnTo: string };

/**
 * Deux implémentations derrière la même interface :
 *
 * - `oidcProvider` — le vrai flow : un `UserManager` par realm, code flow + PKCE S256,
 *   renouvellement silencieux, jetons en sessionStorage ;
 * - `mockProvider` — aucun réseau, utilisé quand `GRAAL_MOCKS=1`. Il n'existe **aucun**
 *   `graal-api` ni Keycloak démarrable dans ce dépôt pour l'instant : sans lui, aucune route
 *   applicative ne serait atteignable, ni en développement ni au smoke Playwright.
 *
 * Le choix est fait par la configuration d'exécution, jamais au build.
 */
type AuthProvider = {
  beginSignIn: (pending: PendingSignIn) => Promise<void>;
  /**
   * Nouvelle connexion INTERACTIVE, même si une session est ouverte (`prompt=login`, `max_age=0`) :
   * le jeton rendu porte un `auth_time` frais. Exigé par les gestes qui veulent une personne
   * ré-authentifiée depuis peu (décision d'une validation d'agent, ADR 0080 : moins de 5 minutes).
   */
  reauthenticate: (pending: PendingSignIn) => Promise<void>;
  completeSignIn: () => Promise<CompletedSignIn>;
  completeSilentRenew: () => Promise<void>;
  restore: (tenantId: string, realm: string) => Promise<Session | null>;
  renew: (tenantId: string, realm: string) => Promise<Session | null>;
  signOut: (tenantId: string, realm: string) => Promise<void>;
  /**
   * Chaque jeton renouvelé EN ARRIÈRE-PLAN (`automaticSilentRenew`) est remis à `listener`.
   * Sans cela, la session garde la copie du jeton de la connexion, qui expire au bout de
   * `accessTokenLifespan` (5 min par défaut) : tous les appels suivants prennent un 401, alors
   * que oidc-client-ts détient déjà un jeton frais. Mesuré sur dev le 2026-09-28.
   */
  watchRenewals: (tenantId: string, realm: string, listener: (session: Session) => void) => () => void;
  /** Administrateur de la PLATEFORME ? Voir `isPlatformAdmin` plus bas. */
  isPlatformAdmin: (session: Session) => boolean;
};

/**
 * Realm de l'administration de la plateforme (locataire `graal-admin`) : `SharedConstants.ADMIN_REALM`
 * côté backend. Tout jeton UTILISATEUR émis par ce realm y est administrateur de la plateforme
 * (`TenantBasedReactiveAuthenticationManagerResolver#getInternalAuthenticationForAdmin` pose
 * `isAdmin = true`, que lit `RbacService#isPlatformAdministrator`) ; aucun jeton d'un autre realm ne
 * l'est. La console n'ouvre que des sessions utilisateur : le realm suffit, sans requête.
 */
export const PLATFORM_ADMIN_REALM = 'admin';

/**
 * Mocks seulement : `sessionStorage['graal.mock.tenants'] = 'forbidden'` simule un utilisateur qui
 * n'est PAS administrateur de la plateforme — le handler MSW de `GET /tenants` rend alors 403, et le
 * fournisseur local le déclare non administrateur. Sans ce drapeau, l'utilisateur fictif l'est.
 */
export const MOCK_NOT_PLATFORM_ADMIN_KEY = 'graal.mock.tenants';

function sessionFromUser(tenantId: string, realm: string, user: User): Session {
  const profile = user.profile;
  const email = typeof profile.email === 'string' ? profile.email : '';
  const name =
    (typeof profile.name === 'string' && profile.name) ||
    (typeof profile.preferred_username === 'string' && profile.preferred_username) ||
    email ||
    tenantId;
  return {
    tenantId,
    realm,
    user: { name, email, initials: initialsOf(name, tenantId) },
    accessToken: user.access_token,
    expiresAt: user.expires_at ?? 0,
  };
}

const oidcProvider: AuthProvider = {
  beginSignIn: async (pending) => {
    setPendingSignIn(pending);
    await userManagerFor(pending.realm).signinRedirect({
      // `state` survit à l'aller-retour Keycloak : c'est la source qui fait foi au retour,
      // sessionStorage n'étant qu'un filet quand le fournisseur ne rend pas le state.
      state: pending,
      // Administrateur de la plateforme : Keycloak saute son écran et part sur l'IdP `admin` du
      // realm du tenant (masqué de la page de connexion, mais joignable avec l'indice).
      ...(pending.idpHint ? { extraQueryParams: { kc_idp_hint: pending.idpHint } } : {}),
    });
  },

  reauthenticate: async (pending) => {
    setPendingSignIn(pending);
    // `prompt=login` force l'écran de connexion de Keycloak (la session SSO ne suffit pas) ;
    // `max_age=0` exige une authentification postérieure à la demande : `auth_time` est donc frais.
    await userManagerFor(pending.realm).signinRedirect({ state: pending, prompt: 'login', max_age: 0 });
  },

  completeSignIn: async () => {
    const pending = takePendingSignIn();
    if (!pending) throw new Error('Aucune connexion en cours : /callback appelé hors du flow.');
    const user = await userManagerFor(pending.realm).signinRedirectCallback();
    const state = (user.state ?? pending) as PendingSignIn;
    const session = sessionFromUser(state.tenantId, state.realm, user);
    return { session, returnTo: state.returnTo };
  },

  completeSilentRenew: async () => {
    // Exécuté dans l'iframe cachée : oidc-client-ts poste le résultat à la fenêtre parente,
    // il n'y a rien à rendre. L'iframe partage l'origine — donc le sessionStorage — de la
    // fenêtre parente : le realm courant y est déjà, inutile de le remettre dans l'URL.
    const last = lastTenant();
    if (!last) throw new Error('Renouvellement silencieux hors session : aucun realm connu.');
    await userManagerFor(last.realm).signinSilentCallback();
  },

  restore: async (tenantId, realm) => {
    const user = await userManagerFor(realm).getUser();
    if (!user || user.expired) return null;
    return sessionFromUser(tenantId, realm, user);
  },

  renew: async (tenantId, realm) => {
    const user = await userManagerFor(realm).signinSilent();
    return user ? sessionFromUser(tenantId, realm, user) : null;
  },

  signOut: async (_tenantId, realm) => {
    const manager = userManagerFor(realm);
    // Plus aucun renouvellement en arrière-plan : si `signoutRedirect` échoue (Keycloak
    // injoignable), la page reste affichée et ne doit pas se remettre à publier des jetons.
    manager.stopSilentRenew();
    await manager.signoutRedirect();
  },

  watchRenewals: (tenantId, realm, listener) => {
    const events = userManagerFor(realm).events;
    const onLoaded = (user: User) => listener(sessionFromUser(tenantId, realm, user));
    events.addUserLoaded(onLoaded);
    return () => events.removeUserLoaded(onLoaded);
  },

  isPlatformAdmin: (session) => session.realm === PLATFORM_ADMIN_REALM,
};

// ---------------------------------------------------------------------------
// Fournisseur local (GRAAL_MOCKS=1) — aucun réseau, même parcours de routes.
// ---------------------------------------------------------------------------

const MOCK_KEY = 'graal.auth.mock';
const MOCK_TTL_SECONDS = 3_600;

function mockSession(tenantId: string, realm: string): Session {
  const name = 'Camille Martin';
  return {
    tenantId,
    realm,
    user: { name, email: `camille.martin@${tenantId}.example`, initials: initialsOf(name, tenantId) },
    accessToken: `mock.${tenantId}.${Math.floor(Date.now() / 1000)}`,
    expiresAt: Math.floor(Date.now() / 1000) + MOCK_TTL_SECONDS,
  };
}

function readMock(tenantId: string, ignoreExpiry = false): Session | null {
  try {
    const raw = window.sessionStorage.getItem(`${MOCK_KEY}.${tenantId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Session;
    return ignoreExpiry || parsed.expiresAt * 1000 > Date.now() ? parsed : null;
  } catch {
    return null;
  }
}

function writeMock(session: Session): void {
  try {
    window.sessionStorage.setItem(`${MOCK_KEY}.${session.tenantId}`, JSON.stringify(session));
  } catch {
    // stockage indisponible : la session ne survivra pas au rechargement
  }
}

const mockProvider: AuthProvider = {
  beginSignIn: async (pending) => {
    setPendingSignIn(pending);
    writeMock(mockSession(pending.tenantId, pending.realm));
    // Même parcours que le vrai flow : on repasse par /callback, qui reste donc testé.
    window.location.assign(CALLBACK_PATH);
    return Promise.resolve();
  },

  // Même parcours qu'une connexion : un jeton fictif NEUF (son horodatage tient lieu d'`auth_time`
  // pour les handlers MSW des validations), puis /callback, qui rouvre `returnTo`.
  reauthenticate: (pending) => {
    setPendingSignIn(pending);
    writeMock(mockSession(pending.tenantId, pending.realm));
    window.location.assign(CALLBACK_PATH);
    return Promise.resolve();
  },

  completeSignIn: async () => {
    const pending = takePendingSignIn();
    if (!pending) throw new Error('Aucune connexion en cours : /callback appelé hors du flow.');
    const session = readMock(pending.tenantId) ?? mockSession(pending.tenantId, pending.realm);
    writeMock(session);
    return Promise.resolve({ session, returnTo: pending.returnTo });
  },

  completeSilentRenew: () => Promise.resolve(),

  restore: (tenantId) => Promise.resolve(readMock(tenantId)),

  renew: (tenantId, realm) => {
    // Renouveler n'est pas se connecter : sans trace d'une connexion précédente, on rend null
    // et la garde de route renvoie sur /login — exactement ce que fait `signinSilent` en OIDC.
    if (!readMock(tenantId, true)) return Promise.resolve(null);
    const session = mockSession(tenantId, realm);
    writeMock(session);
    return Promise.resolve(session);
  },

  signOut: (tenantId) => {
    try {
      window.sessionStorage.removeItem(`${MOCK_KEY}.${tenantId}`);
    } catch {
      // rien à nettoyer
    }
    window.location.assign('/login');
    return Promise.resolve();
  },

  // Les jetons fictifs vivent MOCK_TTL_SECONDS : rien ne se renouvelle en arrière-plan.
  watchRenewals: () => () => undefined,

  isPlatformAdmin: () => {
    try {
      return window.sessionStorage.getItem(MOCK_NOT_PLATFORM_ADMIN_KEY) !== 'forbidden';
    } catch {
      return false;
    }
  },
};

export function authProvider(): AuthProvider {
  return authMode() === 'mock' ? mockProvider : oidcProvider;
}

// ---------------------------------------------------------------------------
// API publique
// ---------------------------------------------------------------------------

/**
 * L'utilisateur de la session est-il administrateur de la PLATEFORME ? `GET /permissions` ne le
 * porte pas (c'est `RbacService#isPlatformAdministrator`, hors RBAC du locataire), et le demander
 * à `GET /tenants` coûtait un 403 — donc une erreur « Failed to load resource » dans la console du
 * navigateur — à chaque écran d'un utilisateur ordinaire (constaté sur dev le 2026-10-15). On le
 * déduit donc de la session, sans requête. Ce n'est pas une frontière de sécurité : le backend
 * refuse de toute façon (403) ; on échoue fermé (pas de session → non).
 */
export function isPlatformAdmin(session: Session | null): boolean {
  return session !== null && authProvider().isPlatformAdmin(session);
}

export async function beginSignIn(pending: PendingSignIn): Promise<void> {
  rememberTenant(pending.tenantId, pending.realm);
  await authProvider().beginSignIn(pending);
}

/** Se reconnecter EXPLICITEMENT (voir `AuthProvider.reauthenticate`), puis revenir sur `returnTo`. */
export async function reauthenticate(pending: PendingSignIn): Promise<void> {
  rememberTenant(pending.tenantId, pending.realm);
  await authProvider().reauthenticate(pending);
}

/**
 * Mémorisé pour toute la durée du document : le code d'autorisation ne peut être échangé
 * qu'UNE fois (Keycloak refuse le second avec « Code already used »). Or React en mode strict
 * monte, démonte puis remonte l'effet du composant /callback — sans cette mémorisation, la
 * seconde exécution échouerait et l'écran afficherait « connexion interrompue » alors que la
 * connexion a réussi. Même protection pour un double rendu ou un retour arrière.
 */
let signInCompletion: Promise<CompletedSignIn> | null = null;

export function completeSignIn(): Promise<CompletedSignIn> {
  signInCompletion ??= authProvider()
    .completeSignIn()
    .then((completed) => {
      rememberTenant(completed.session.tenantId, completed.session.realm);
      publishSession(completed.session);
      return completed;
    })
    .catch((error: unknown) => {
      signInCompletion = null; // un échec doit pouvoir être rejoué depuis /login
      throw error;
    });
  return signInCompletion;
}

export async function completeSilentRenew(): Promise<void> {
  await authProvider().completeSilentRenew();
}

export async function restoreSession(tenantId: string, realm: string): Promise<Session | null> {
  const session = await authProvider().restore(tenantId, realm);
  publishSession(session);
  return session;
}

/**
 * Renouvellement partagé : plusieurs requêtes qui prennent un 401 en même temps ne déclenchent
 * qu'un seul `signinSilent` (mutex repris de recrutauto/apiFetch.tsx).
 */
let renewal: Promise<Session | null> | null = null;

export function renewSession(tenantId: string, realm: string): Promise<Session | null> {
  renewal ??= authProvider()
    .renew(tenantId, realm)
    .then((session) => {
      publishSession(session);
      return session;
    })
    .catch(() => null)
    .finally(() => {
      renewal = null;
    });
  return renewal;
}

export function watchRenewals(
  tenantId: string,
  realm: string,
  listener: (session: Session) => void,
): () => void {
  return authProvider().watchRenewals(tenantId, realm, listener);
}

export async function signOut(tenantId: string, realm: string): Promise<void> {
  publishSession(null);
  forgetTenant();
  await authProvider().signOut(tenantId, realm);
}
